// Inheritance (Faraid) worksheet — blank Word template for the Estate
// Planning Kit. Mirrors the data the in-app Inheritance wizard collects so
// a user can fill it by hand and bring it to a scholar.

const stamp = () => new Date().toISOString().slice(0, 10);
const loadDocx = () => import('docx');

// Amanah brand theme — shared header/footer/cover across every Kit doc.
import { buildAmanahSection } from './amanahDocxTheme';

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const downloadInheritanceWorksheetDocx = async (_state = null, { asBlob = false } = {}) => {
  const docx = await loadDocx();
  const { Document, Packer, Paragraph, TextRun, AlignmentType, UnderlineType,
    Table, TableRow, TableCell, WidthType, BorderStyle } = docx;

  const BORDERS = {
    top: { style: BorderStyle.SINGLE, size: 4, color: 'AAAAAA' },
    bottom: { style: BorderStyle.SINGLE, size: 4, color: 'AAAAAA' },
    left: { style: BorderStyle.SINGLE, size: 4, color: 'AAAAAA' },
    right: { style: BorderStyle.SINGLE, size: 4, color: 'AAAAAA' },
    insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: 'CCCCCC' },
    insideVertical: { style: BorderStyle.SINGLE, size: 2, color: 'CCCCCC' },
  };

  const cell = (text, opts = {}) => new TableCell({
    width: { size: opts.w || 50, type: WidthType.PERCENTAGE },
    children: [new Paragraph({ children: [new TextRun({ text: String(text), bold: !!opts.bold, size: 22 })] })],
  });
  const blank = (w = 40) => new TextRun({
    text: '\u00A0'.repeat(w),
    underline: { type: UnderlineType.SINGLE, color: '888888' },
    size: 22,
  });
  const h = (t) => new Paragraph({ spacing: { before: 240, after: 120 },
    children: [new TextRun({ text: t, bold: true, size: 26, color: '0F4C3A' })] });
  const p = (t, opts = {}) => new Paragraph({ spacing: { after: 100 },
    children: [new TextRun({ text: t, size: 22, ...opts })] });
  const lbl = (label) => new Paragraph({
    spacing: { after: 160 },
    children: [new TextRun({ text: `${label}: `, bold: true, size: 22 }), blank(60)],
  });

  // Heir table: relation + count with 0 blanks for handwriting
  const heirRows = [
    new TableRow({ children: [cell('Heir / relation', { bold: true, w: 70 }), cell('Count', { bold: true, w: 30 })] }),
    ...['Husband', 'Wife (1, 2, 3 or 4)', 'Father', 'Mother', 'Son', 'Daughter',
        "Son's son (paternal grandson)", "Son's daughter (paternal granddaughter)",
        'Full brother', 'Full sister', 'Paternal half-brother', 'Paternal half-sister',
        'Maternal half-brother', 'Maternal half-sister', "Father's father (paternal grandfather)",
        "Mother's mother (maternal grandmother)", "Father's mother (paternal grandmother)",
        'Other (dhawu al-arham — write below)']
      .map((label) => new TableRow({ children: [cell(label, { w: 70 }), cell(' ', { w: 30 })] })),
  ];

  // Asset valuation table (10 blank rows)
  const valuationRows = [
    new TableRow({ children: [
      cell('Asset / item', { bold: true, w: 45 }),
      cell('Value at death', { bold: true, w: 22 }),
      cell('Value at distribution', { bold: true, w: 22 }),
      cell('Owner % (if joint)', { bold: true, w: 11 }),
    ] }),
    ...Array.from({ length: 12 }, () => new TableRow({ children: [
      cell(' ', { w: 45 }), cell(' ', { w: 22 }), cell(' ', { w: 22 }), cell(' ', { w: 11 }),
    ] })),
  ];

  // Deduction table
  const deductionRows = [
    new TableRow({ children: [cell('Deduction', { bold: true, w: 70 }), cell('Amount', { bold: true, w: 30 })] }),
    ...['Funeral & burial costs', 'Outstanding debts to people',
        'Outstanding debts to Allah (unpaid Zakat, kaffarah)',
        'Unpaid Mahr', 'Wasiyyah / bequests (≤ 1/3 of net estate)']
      .map((label) => new TableRow({ children: [cell(label, { w: 70 }), cell(' ', { w: 30 })] })),
  ];

  const doc = new Document({ sections: [buildAmanahSection(docx, {
    docTitle: 'ISLAMIC INHERITANCE (FARAID) WORKSHEET',
    children: [
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 80 },
        children: [new TextRun({ text: 'بسم الله الرحمن الرحيم', size: 28 })] }),
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 200 },
        children: [new TextRun({ text: 'ISLAMIC INHERITANCE (FARAID) WORKSHEET', bold: true, size: 32 })] }),
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 200 },
        children: [new TextRun({ text: 'Blank template. Fill by hand, then bring it to a qualified Islamic scholar for verification.', italics: true, size: 20, color: '666666' })] }),
      p('This worksheet collects everything a faraid scholar needs to compute the shares correctly. Filling it BEFORE the meeting saves time and reduces mistakes.', { italics: true }),

      h('1 · Deceased (al-mawrūth)'),
      lbl('Full legal name'),
      lbl('Date of death (Hijri)'),
      lbl('Date of death (Gregorian)'),
      lbl('Place of death'),
      lbl('Madhab the family follows (Hanafi / Maliki / Shafiʿi / Hanbali / Jaʿfari)'),

      h('2 · Estate — assets & liabilities'),
      p('List every asset and its value as at the date of death. Use the second value column only if a later distribution date is used (e.g. for property that has changed value).'),
      new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: BORDERS, rows: valuationRows }),

      h('3 · Deductions (in this exact order, per Sharia)'),
      p('1. Funeral & burial costs.  2. Debts to people.  3. Debts to Allah (unpaid Zakat, kaffarah).  4. Unpaid Mahr.  5. Wasiyyah / bequests — capped at 1/3 of the NET estate after the first four.'),
      new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: BORDERS, rows: deductionRows }),

      h('4 · Heirs'),
      p('Write the COUNT for each category that survived the deceased. Leave 0 or blank where none survived. Note the husband-vs-wife mutual exclusion: a deceased can leave EITHER a husband OR up to four wives, not both.'),
      new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: BORDERS, rows: heirRows }),

      h('5 · Predeceased descendants (for wajib wasiyyah, if your jurisdiction applies it)'),
      p('Some jurisdictions (Egypt 1946, Pakistan MFLO 1961, Tunisia, Algeria, Jordan, Morocco) add a wajib wasiyyah for orphaned grandchildren whose own parent predeceased the deceased.'),
      lbl('Predeceased son/daughter name + descendants'),
      lbl('Predeceased son/daughter name + descendants'),
      lbl('Predeceased son/daughter name + descendants'),

      h('6 · Lifetime gifts (hiba) — informational'),
      p('Per the majority view, valid hiba completed in the donor\u2019s lifetime is NOT recalled into the estate. Listed here only for transparency.'),
      lbl('Gift 1 (recipient, value, date)'),
      lbl('Gift 2'),
      lbl('Gift 3'),

      h('7 · Waqf / Awqaf'),
      p('LIFETIME waqf is OUTSIDE the estate entirely. TESTAMENTARY waqf counts against the 1/3 wasiyyah cap.'),
      lbl('Waqf 1 (cause, amount, timing)'),
      lbl('Waqf 2'),

      h('Reminder'),
      p('This worksheet is a data-collection aid only. The faraid math itself MUST be done by a qualified scholar or the deterministic in-app calculator at zakat.international/inheritance. Never apply shares from informal sources.', { italics: true, color: '666666' }),

      new Paragraph({ spacing: { before: 300 },
        children: [new TextRun({ text: 'Filled by: ', bold: true, size: 22 }), blank(40)] }),
      new Paragraph({ spacing: { before: 100 },
        children: [new TextRun({ text: 'Date: ', bold: true, size: 22 }), blank(30)] }),
    ],
  })] });

  const blob = await Packer.toBlob(doc);
  if (asBlob) return blob;
  downloadBlob(blob, `inheritance-worksheet-blank-${stamp()}.docx`);
  return blob;
};

/**
 * Blank Inheritance (Faraid) worksheet — .csv
 * Simple spreadsheet you can fill on any device. Row structure mirrors the
 * DOCX so a scholar can key it into the deterministic calculator later.
 */
export const downloadInheritanceWorksheetCsv = (_state = null, { asBlob = false } = {}) => {
  const rows = [
    ['Amanah Inheritance (Faraid) Worksheet', '', '', 'Generated', stamp()],
    [],
    ['1. Deceased'],
    ['Full name', ''],
    ['Date of death', ''],
    ['Place of death / jurisdiction', ''],
    ['Madhhab (Hanafi / Maliki / Shafi\'i / Hanbali / Ja\'fari)', ''],
    [],
    ['2. Gross estate on the moment of death', 'Value'],
    ['Cash / bank balances', ''],
    ['Real estate', ''],
    ['Business ownership stakes', ''],
    ['Vehicles', ''],
    ['Investments (stocks / crypto / bonds)', ''],
    ['Personal effects & household', ''],
    ['Other receivables owed TO the deceased', ''],
    ['TOTAL GROSS ESTATE', '=SUM(B10:B16)'],
    [],
    ['3. Deductions (paid BEFORE distribution)', 'Amount'],
    ['Funeral & burial costs', ''],
    ['Debts to creditors', ''],
    ['Unpaid Zakat (debt to Allah)', ''],
    ['Unpaid mahr owed to widow', ''],
    ['Wasiyyah bequests (capped at 1/3 of net)', ''],
    ['TOTAL DEDUCTIONS', '=SUM(B20:B24)'],
    [],
    ['Net distributable estate', '=B17-B25'],
    [],
    ['4. Surviving heirs — tick each ALIVE at the moment of death', 'Count', 'Notes'],
    ['Husband', '', ''],
    ['Wife (up to 4)', '', ''],
    ['Father', '', ''],
    ['Mother', '', ''],
    ['Son', '', ''],
    ['Daughter', '', ''],
    ["Son's son (paternal grandson)", '', ''],
    ["Son's daughter (paternal granddaughter)", '', ''],
    ['Full brother', '', ''],
    ['Full sister', '', ''],
    ['Paternal half-brother', '', ''],
    ['Paternal half-sister', '', ''],
    ['Maternal half-brother', '', ''],
    ['Maternal half-sister', '', ''],
    ['Paternal grandfather', '', ''],
    ['Paternal grandmother', '', ''],
    ['Maternal grandmother', '', ''],
    ['Other (specify)', '', ''],
    [],
    ['5. Orphaned grandchildren (for Wajib Wasiyyah toggle)'],
    ['Predeceased son / daughter name', ''],
    ['Their surviving children (count)', ''],
    [],
    ['6. Waqf declared during life?', ''],
    ['Amount / cause', ''],
    ['Testamentary waqf (counts toward 1/3 cap)?', ''],
    [],
    ['Filled by', ''],
    ['Date', ''],
  ];

  const esc = (v) => {
    const s = String(v ?? '');
    return /["\n,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((r) => r.map(esc).join(',')).join('\n') + '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  if (asBlob) return blob;
  downloadBlob(blob, `inheritance-worksheet-${stamp()}.csv`);
  return null;
};
